import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, formatMoney, formatDate } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, MagnifyingGlass, Scales, Gavel } from '@phosphor-icons/react';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  filed: 'bg-indigo-100 text-indigo-700',
  in_hearing: 'bg-orange-100 text-orange-700',
  judgment: 'bg-purple-100 text-purple-700',
  execution: 'bg-violet-100 text-violet-700',
  closed: 'bg-emerald-100 text-emerald-700',
  settled: 'bg-teal-100 text-teal-700',
};

export default function LegalClaims() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ claim_types: [], statuses: [] });
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newClaim, setNewClaim] = useState({ claim_type: 'financial', title: '', claim_amount: 0, tenant_name: '', contract_no: '', branch: '', notes: '' });

  const filters = {
    status: params.get('status') || 'all',
    claim_type: params.get('claim_type') || 'all',
    branch: params.get('branch') || '',
  };
  const updFilter = (k, v) => {
    const nx = new URLSearchParams(params);
    if (!v || v === 'all') nx.delete(k); else nx.set(k, v);
    setParams(nx);
  };

  const load = () => {
    setLoading(true);
    const q2 = {};
    if (filters.status !== 'all') q2.status = filters.status;
    if (filters.claim_type !== 'all') q2.claim_type = filters.claim_type;
    if (filters.branch) q2.branch = filters.branch;
    api.get('/legal/claims', { params: q2 }).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [params.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/legal/settings').then(({ data }) => setSettings(data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter(i =>
      (i.claim_no || '').toLowerCase().includes(s) ||
      (i.tenant_name || '').toLowerCase().includes(s) ||
      (i.contract_no || '').toLowerCase().includes(s) ||
      (i.case_no || '').toLowerCase().includes(s)
    );
  }, [items, q]);

  const statusLabel = (code) => settings.statuses.find(s => s.code === code)?.label_ar || code;
  const typeLabel = (code) => settings.claim_types.find(t => t.code === code || t.code === (code || '').toUpperCase())?.label_ar || code;

  const createClaim = async () => {
    try {
      const { data } = await api.post('/legal/claims', newClaim);
      toast.success(`تم إنشاء المطالبة ${data.claim_no}`);
      setCreateOpen(false);
      setNewClaim({ claim_type: 'financial', title: '', claim_amount: 0, tenant_name: '', contract_no: '', branch: '', notes: '' });
      load();
    } catch (e) {
      toast.error('فشل الإنشاء');
    }
  };

  return (
    <div className="space-y-6" data-testid="legal-claims-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#1E3A5F] mb-1">Legal Claims</div>
          <h1 className="text-3xl font-bold text-slate-900">إدارة المطالبات القانونية</h1>
          <p className="text-sm text-slate-500 mt-1">جميع المطالبات المسجلة مع فلترة حسب الحالة والنوع والفرع</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 text-white" data-testid="new-claim-btn">
          <Plus className="ms-1" size={16} /> مطالبة جديدة
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 border border-slate-200 rounded-md shadow-none">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">الحالة</label>
            <Select value={filters.status} onValueChange={(v) => updFilter('status', v)}>
              <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                {settings.statuses.map(s => <SelectItem key={s.code} value={s.code}>{s.label_ar}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">نوع المطالبة</label>
            <Select value={filters.claim_type} onValueChange={(v) => updFilter('claim_type', v)}>
              <SelectTrigger className="h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="financial">مطالبة مالية</SelectItem>
                <SelectItem value="eviction">مطالبة إخلاء</SelectItem>
                <SelectItem value="execution">مطالبة تنفيذ</SelectItem>
                <SelectItem value="judicial">مطالبة قضائية</SelectItem>
                <SelectItem value="custom">مطالبة مخصصة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">الفرع</label>
            <Input value={filters.branch} onChange={(e) => updFilter('branch', e.target.value)} className="h-9" placeholder="اسم الفرع" data-testid="filter-branch" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 mb-1 block">بحث</label>
            <div className="relative">
              <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pr-9 h-9" placeholder="رقم المطالبة، المستأجر، العقد، رقم الدعوى" data-testid="search-input" />
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="p-3 text-start">رقم المطالبة</th>
                <th className="p-3 text-start">النوع</th>
                <th className="p-3 text-start">المستأجر</th>
                <th className="p-3 text-start">العقد</th>
                <th className="p-3 text-start">الفرع</th>
                <th className="p-3 text-end">المطالب به</th>
                <th className="p-3 text-start">الحالة</th>
                <th className="p-3 text-start">الموظف</th>
                <th className="p-3 text-start">آخر إجراء</th>
                <th className="p-3 text-start">الجلسة القادمة</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400">جارٍ التحميل...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-12 text-center text-slate-400">
                  <Scales className="mx-auto mb-2 text-slate-300" size={32} />
                  لا توجد مطالبات
                </td></tr>
              ) : filtered.map(c => (
                <tr
                  key={c.id}
                  onClick={() => nav(`/legal/claims/${c.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`claim-row-${c.claim_no}`}
                >
                  <td className="p-3 font-mono text-slate-900 font-semibold">{c.claim_no}</td>
                  <td className="p-3">{typeLabel((c.claim_type || 'CUST').toUpperCase())}</td>
                  <td className="p-3 text-slate-900">{c.tenant_name || '—'}</td>
                  <td className="p-3 font-mono">{c.contract_no || '—'}</td>
                  <td className="p-3">{c.branch || '—'}</td>
                  <td className="p-3 text-end tabular-nums font-semibold">{formatMoney(c.claim_amount)}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLES[c.status] || 'bg-slate-100'}`}>
                      {statusLabel(c.status)}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{c.assigned_to_name || '—'}</td>
                  <td className="p-3 text-xs text-slate-500">{c.last_action ? `${c.last_action} · ${formatDate(c.last_action_date)}` : '—'}</td>
                  <td className="p-3 text-xs">{c.next_hearing_date ? formatDate(c.next_hearing_date) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader><DialogTitle>مطالبة قانونية جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm mb-1 block">نوع المطالبة</Label>
              <Select value={newClaim.claim_type} onValueChange={(v) => setNewClaim({ ...newClaim, claim_type: v })}>
                <SelectTrigger className="h-9" data-testid="new-claim-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="financial">مطالبة مالية</SelectItem>
                  <SelectItem value="eviction">مطالبة إخلاء</SelectItem>
                  <SelectItem value="execution">مطالبة تنفيذ</SelectItem>
                  <SelectItem value="judicial">مطالبة قضائية</SelectItem>
                  <SelectItem value="custom">مطالبة مخصصة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">عنوان المطالبة</Label>
              <Input value={newClaim.title} onChange={(e) => setNewClaim({ ...newClaim, title: e.target.value })} placeholder="اختياري" data-testid="new-claim-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm mb-1 block">اسم المستأجر</Label>
                <Input value={newClaim.tenant_name} onChange={(e) => setNewClaim({ ...newClaim, tenant_name: e.target.value })} data-testid="new-claim-tenant" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">رقم العقد</Label>
                <Input value={newClaim.contract_no} onChange={(e) => setNewClaim({ ...newClaim, contract_no: e.target.value })} data-testid="new-claim-contract" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">الفرع</Label>
                <Input value={newClaim.branch} onChange={(e) => setNewClaim({ ...newClaim, branch: e.target.value })} data-testid="new-claim-branch" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">قيمة المطالبة (ر.س)</Label>
                <Input type="number" value={newClaim.claim_amount} onChange={(e) => setNewClaim({ ...newClaim, claim_amount: parseFloat(e.target.value) || 0 })} data-testid="new-claim-amount" />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">ملاحظات</Label>
              <Textarea value={newClaim.notes} onChange={(e) => setNewClaim({ ...newClaim, notes: e.target.value })} rows={3} data-testid="new-claim-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>إلغاء</Button>
            <Button className="bg-slate-900 text-white" onClick={createClaim} data-testid="new-claim-submit">
              <Gavel className="ms-1" size={14} /> إنشاء المطالبة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
